import { Component, Prop, State, h } from '@stencil/core';
import { io, Socket } from 'socket.io-client';
import { marked } from 'marked';

@Component({
  tag: 'hash-bot',
  styleUrl: 'hash-bot.css',
  shadow: true,
})
export class HashBot {
  @Prop() bot_name: string;
  @Prop() agent_uuid: string;
  @Prop() welcome_message: string;
  @Prop() iconsize: number = 40;
  @Prop() chatbotwidth: number = 300;
  @Prop() chatbotheight: number = 400;

  @State() messages: { text: string; sender: 'user' | 'bot'; id: string }[] = [];
  @State() isChatOpen: boolean = false;
  @State() isMinimized: boolean = false;
  @State() unreadMessages: number = 0;
  @State() isBotTyping: boolean = false;
  @State() isSending: boolean = false;
  @State() chatId: string = '';
  @State() hasStarted: boolean = false;
  @State() exampleQuestions: { question: string; id: string }[] = [];
  @State() inputValue: string = '';
  @State() apiurl: string = 'https://chatbot.prod.alphainterface.ai';
  @State() socketStatus = 'offline';
  @State() theme: 'dark' | 'light' = 'dark';
  @State() completedWordCounts: { [id: string]: number } = {};

  private socket: Socket;
  private inputRef: HTMLInputElement;
  private messagesEndRef: HTMLElement;

  // Utility function to generate unique session-like ID
  private generateChatId(): string {
    const letters = Math.random().toString(36).substring(2, 5);
    const numbers = Math.floor(100 + Math.random() * 900);
    return `session-${letters}${numbers}`;
  }

  async componentWillLoad() {
    // Retrieve or generate chat_id
    const existingChatId = sessionStorage.getItem("alpha-chatbot-session-id");
    if (existingChatId) {
      this.chatId = existingChatId;
    } else {
      const newChatId = this.generateChatId();
      sessionStorage.setItem("alpha-chatbot-session-id", newChatId);
      this.chatId = newChatId;
    }

    // Fetch example questions if agent_uuid is provided
    if (this.agent_uuid) {
      try {
        const res = await fetch(this.apiurl + `/api/agents/example-questions/${this.agent_uuid}`);
        const json = await res.json();
        this.exampleQuestions = Array.isArray(json.data) ? json.data : [];
      } catch (err) {
        console.error("Failed to fetch example questions", err);
      }
    }

    // Set initial bot message
    if (this.messages.length === 0) {
      this.addMessage(this.welcome_message || "Hello! How can I help you today?", 'bot');
    }
  }

  connectedCallback() {
    this.socket = io(this.apiurl, {
      transports: ['websocket', 'polling'],
    });

    this.socket.on('connect', () => {
      this.socketStatus = 'online';
      console.log('Connected to Socket.IO server');
    });

    this.socket.on('answer', (data: any) => {
      if (data?.answer) {
        this.isBotTyping = false;
        this.isSending = false;
        this.addMessage(data.answer, 'bot');
      }
    });

    this.socket.on('disconnect', () => {
      this.socketStatus = 'offline';
      console.log('Disconnected from Socket.IO server');
    });
  }

  addMessage(text: string, sender: 'user' | 'bot') {
    if (!text) return;
    const id = Math.random().toString(36).substring(2, 9);

    if (sender === 'user') {
      this.messages = [...this.messages, { text, sender, id }];
      this.scrollToBottom();
    } else {
      // For bot, simulate streaming
      const initialBotMessage = { text: '', sender, id } as any;
      this.messages = [...this.messages, initialBotMessage];

      const words = text.split(/(\s+)/);
      let currentWordIndex = 0;
      let displayedText = '';

      const typeNextWord = () => {
        if (currentWordIndex < words.length) {
          displayedText += words[currentWordIndex];

          // Update the message in state
          this.messages = this.messages.map(m =>
            m.id === id ? { ...m, text: displayedText } : m
          );

          // Update word count tracking
          const totalWords = displayedText.split(/\s+/).filter(w => w.length > 0).length;
          this.completedWordCounts = { ...this.completedWordCounts, [id]: totalWords };

          currentWordIndex++;
          this.scrollToBottom();

          // Adaptive speed: faster for long spaces/whitespace
          const delay = words[currentWordIndex - 1].trim() ? 30 : 10;
          setTimeout(typeNextWord, delay);
        } else {
          if (!this.isChatOpen) {
            this.unreadMessages++;
          }
        }
      };

      typeNextWord();
    }
  }

  private scrollToBottom() {
    setTimeout(() => {
      if (this.messagesEndRef) {
        this.messagesEndRef.scrollTop = this.messagesEndRef.scrollHeight;
      }
    }, 50);
  }

  private renderBotMessage(html: string, messageId: string) {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    let wordIndex = 0;
    const previousCount = this.completedWordCounts[messageId] || 0;

    const processNodes = (node: Node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const words = node.textContent.split(/(\s+)/);
        const fragment = document.createDocumentFragment();

        words.forEach(word => {
          if (word.trim()) {
            const span = document.createElement('span');
            // Only animate words that are "new"
            const isNew = wordIndex >= previousCount - 2; // small buffer
            span.className = isNew ? 'bot-word' : 'bot-word-static';

            if (isNew) {
              // Stagger delay based on how many "new" words we've seen in this render
              const newWordIdx = Math.max(0, wordIndex - previousCount);
              span.style.animationDelay = `${newWordIdx * 0.05}s`;
            }

            span.textContent = word;
            fragment.appendChild(span);
            wordIndex++;
          } else {
            fragment.appendChild(document.createTextNode(word));
          }
        });

        node.parentNode.replaceChild(fragment, node);
      } else {
        Array.from(node.childNodes).forEach(child => processNodes(child));
      }
    };

    processNodes(tempDiv);

    // Update the count for the NEXT render if we are at the end of a typeNextWord cycle
    // We don't update this.completedWordCounts directly here to avoid re-renders during render

    return tempDiv.innerHTML;
  }

  toggleChat() {
    this.isChatOpen = !this.isChatOpen;
    if (this.isChatOpen) {
      this.isMinimized = false;
      this.unreadMessages = 0;
    }
  }

  toggleTheme() {
    this.theme = this.theme === 'dark' ? 'light' : 'dark';
  }

  handleSendMessage(query?: string) {
    if (this.isSending) return;

    const message = (query || this.inputValue).trim();
    if (!message) return;

    this.hasStarted = true;
    this.addMessage(message, 'user');
    this.isBotTyping = true;
    this.isSending = true;
    this.inputValue = '';
    if (this.inputRef) this.inputRef.value = '';

    if (this.socket && this.socketStatus === 'online') {
      this.socket.emit('ask', {
        message: message,
        agent_uuid: this.agent_uuid,
        chat_id: this.chatId,
      });
    } else {
      console.error("Socket not connected");
      this.isBotTyping = false;
      this.isSending = false;
      this.addMessage("Messenger connection lost. Please refresh or try again later.", 'bot');
    }
  }

  render() {
    return (
      <div class={`hash-bot theme-${this.theme}`}>
        <div class="widget-container">
          {/* Minimized Bar */}
          {this.isChatOpen && this.isMinimized && (
            <div class="minimized-bar" onClick={() => this.isMinimized = false}>
              <div class="status-indicator"></div>
              <span class="agent-name-min">{this.bot_name || "Alpha Assistant"}</span>
              <svg class="maximize-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="m15 3 6 6-6 6" opacity="0.4" />
                <path d="m9 21-6-6 6-6" />
              </svg>
            </div>
          )}

          {/* Floating Toggle Button */}
          {!this.isChatOpen && (
            <button class="floating-toggle" onClick={() => this.toggleChat()}>
              <div class="toggle-gradient"></div>
              <svg class="toggle-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z" />
                <path d="M12 8v4M12 16h.01" opacity="0.4" />
              </svg>
              <span class="toggle-label">Chat with us</span>
            </button>
          )}

          {/* Main Chat Widget */}
          {this.isChatOpen && !this.isMinimized && (
            <div class={`chat-widget chat-widget-enter theme-${this.theme}`}>
              {/* Background Orbs */}
              <div class="orb orb-top-right"></div>
              <div class="orb orb-bottom-left"></div>
              <div class="orb orb-center"></div>

              {/* Header */}
              <div class="chat-header">
                <div class="header-agency">
                  <button class="theme-toggle-btn" onClick={() => this.toggleTheme()}>
                    {this.theme === 'dark' ? (
                      <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <circle cx="12" cy="12" r="4" opacity="0.4" />
                        <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
                      </svg>
                    ) : (
                      <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" />
                        <circle cx="12" cy="12" r="10" opacity="0.1" />
                      </svg>
                    )}
                  </button>
                  <div class="header-info">
                    <h3>{this.bot_name || "Alpha Assistant"}</h3>
                    <div class="header-status">
                      <span class="status-dot"></span>
                      <span class="status-text">Always Active</span>
                    </div>
                  </div>
                </div>
                <div class="header-actions">
                  <button class="action-btn" onClick={() => this.isMinimized = true}>
                    <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <rect width="18" height="18" x="3" y="3" rx="2" opacity="0.4" />
                      <path d="M7 12h10" />
                    </svg>
                  </button>
                  <button class="action-btn close" onClick={() => this.isChatOpen = false}>
                    <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <circle cx="12" cy="12" r="10" opacity="0.4" />
                      <path d="m15 9-6 6M9 9l6 6" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Messages Area */}
              <div class="messages-area" ref={el => this.messagesEndRef = el}>
                {this.messages.map((msg) => (
                  <div key={msg.id} class={`message-row ${msg.sender}`}>
                    {msg.sender === 'bot' && (
                      <div class="bot-avatar-message">
                        <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                          <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                          <circle cx="12" cy="12" r="4" opacity="0.4" />
                        </svg>
                      </div>
                    )}
                    {msg.text && (
                      <div class="message-bubble">
                        {msg.sender === 'bot' ? (
                          <div class="markdown-content" innerHTML={this.renderBotMessage(marked.parse(msg.text) as string, msg.id)} />
                        ) : (
                          msg.text
                        )}
                      </div>
                    )}
                  </div>
                ))}

                {this.isBotTyping && (
                  <div class="message-row bot typing">
                    <div class="bot-avatar-message">
                      <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                        <circle cx="12" cy="12" r="4" opacity="0.4" />
                      </svg>
                    </div>
                    <div class="message-bubble typing-indicator">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                )}
              </div>

              {/* Input Area */}
              <div class="input-area">
                <div class="input-wrapper">
                  <div class="input-container">
                    <input
                      type="text"
                      class="message-input"
                      placeholder="Ask me anything..."
                      ref={el => this.inputRef = el}
                      value={this.inputValue}
                      onInput={e => this.inputValue = (e.target as HTMLInputElement).value}
                      onKeyDown={e => e.key === "Enter" && this.handleSendMessage()}
                    />
                  </div>
                  <button
                    class="send-btn"
                    disabled={!this.inputValue.trim() || this.isSending}
                    onClick={() => this.handleSendMessage()}
                  >
                    {this.isSending ? (
                      <svg class="icon animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M21 12a9 9 0 1 1-6.219-8.56"></path>
                      </svg>
                    ) : (
                      <svg class="icon send-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="m22 2-7 20-4-9-9-4Z" />
                        <path d="M22 2 11 13" opacity="0.4" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
}
